import React from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setModalState, setModalOpen, modalViewsConst } from "../../store/modalSlice";
import { AppDispatch } from "../../store";
import { ReactComponent as CheckmarkIcon } from "../../assets/icons/checkmark-new.svg";
import { ReactComponent as InfoCircleIcon } from "../../assets/icons/info-circle.svg";

interface ChecklistStepProps {
  stepNumber: number;
  description: string;
  isComplete: boolean;
  isLocked?: boolean;
  ctaPath?: string;
  hints?: string[];
  onStepClick?: () => void;
}

const ChecklistStep = ({
  stepNumber,
  description,
  isComplete,
  isLocked = false,
  ctaPath,
  hints,
  onStepClick,
}: ChecklistStepProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const isInteractive = !!ctaPath && !isLocked && !isComplete;
  const canShowHints = !!hints?.length && !isComplete;

  const handleClick = () => {
    if (isInteractive && ctaPath) {
      onStepClick?.();
      dispatch(setModalOpen(false));
      navigate(ctaPath);
    }
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isInteractive) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <div className="flex flex-col relative">
      {/* Card — indicator lives inside on the left */}
      <div
        onClick={isInteractive ? handleClick : undefined}
        onKeyDown={isInteractive ? handleKeyDown : undefined}
        role={isInteractive ? "button" : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        className={`flex items-stretch gap-0 border rounded-md overflow-hidden bg-white transition-colors ${
          isComplete
            ? "border-eai-primary-accent"
            : isLocked
            ? "border-gray-100 opacity-60"
            : "border-gray-200 hover:border-gray-300"
        } ${isInteractive ? "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-eai-primary-accent focus-visible:ring-offset-2" : ""}`}
      >
        {/* Indicator */}
        <div className={`w-16 flex items-center justify-center flex-shrink-0 rounded-r-md border-r border-gray-200 ${isComplete ? "bg-eai-checklist-lt" : "bg-gray-100"}`}>
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center ${
              isComplete ? "bg-eai-primary-accent" : "bg-gray-200"
            }`}
          >
            {isComplete ? (
              <CheckmarkIcon className="w-4 h-4 text-white" style={{ "--stroke-0": "white" } as any} />
            ) : (
              <span className="text-sm font-semibold text-gray-400">{stepNumber}</span>
            )}
          </div>
        </div>

        {/* Text */}
        <div className="flex-1 py-5 px-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 font-Inter">{description}</p>
          </div>
          <div className="flex items-center gap-2 ml-2">
            {isLocked && (
              <span className="text-xs bg-gray-200 text-gray-400 px-2 py-0.5 rounded-full whitespace-nowrap font-Inter">
                Coming soon
              </span>
            )}
            {canShowHints && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(setModalState({
                    modalOpen: true,
                    modalView: modalViewsConst.checklistHints,
                    properties: { stepNumber, description, hints },
                  }));
                }}
                className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                aria-label="Show step instructions"
              >
                <InfoCircleIcon className="w-4 h-4" style={{ "--stroke-0": "currentColor" } as any} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChecklistStep;
